import SVG from "react-inlinesvg";
import { Link } from "react-router-dom";
import cx from "classnames";

import Preloader from "../Preloader";

import styles from "./Button.scss";

interface ButtonProps extends Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, "form"> {
  type?: "button" | "submit";
  onClick?: React.MouseEventHandler<HTMLElement> | React.FormEventHandler;
  children: React.ReactNode;
  variant?: "filled" | "clean" | "light" | "outlined";
  size?: "small" | "medium" | "large";
  isDisabled?: boolean;
  iconLeft?: string;
  iconRight?: string;
  isLoading?: boolean;
  customStyles?: string;
  asLink?: boolean;
  asRouterLink?: boolean;
  href?: string;
}

const Button = ({
  type = "button",
  onClick,
  children,
  variant = "filled",
  size = "large",
  isDisabled,
  iconLeft,
  iconRight,
  isLoading,
  asLink,
  asRouterLink,
  href,
  customStyles,
  form,
  ...rest
}: ButtonProps) => {
  const classes = cx(styles.container, styles[variant], styles[size], customStyles, {
    [styles.isDisabled]: isDisabled
  });

  const content = isLoading ? (
    <Preloader size="small" inverted={variant === "filled"} />
  ) : (
    <>
      {iconLeft && <SVG className={styles.icon} src={iconLeft} />}
      <span className={cx(styles.text)}>{children}</span>
      {iconRight && <SVG className={styles.icon} src={iconRight} />}
    </>
  );

  // Span is used when disabled to prevent click events
  if (isDisabled) {
    return <span className={classes}>{content}</span>;
  }

  if (asRouterLink && href) {
    return (
      <Link className={classes} to={href} {...rest}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a className={classes} href={href} {...rest}>
        {content}
      </a>
    );
  }

  return (
    <button className={classes} type={type} onClick={onClick} form={form} {...rest}>
      {content}
    </button>
  );
};

export default Button;
